/* eslint-disable no-unused-vars */
/**
 * RENDERLESS COMPONENT FOR TABLES
 * THIS COMPONENT RETURNS METHODS AND PROPERTIES REQUIRED TO BUILD THE UI OF A TABLE
 */
import Vue from 'vue';
import { filterString, sortSearch } from './filtertypes';

// const filterKeys = ['value', 'ascending', 'type'];
export default {
  props: {
    /**
     * A flat array of strings or objects as the column names
     * If an object is used, the key should be 'value'
     */
    columns: {
      type: Array,
      required: true,
    },
    /**
     * A 2D array or an array of objects!
     * an array of objects is mandated for easy Data Manipulation
     */
    value: {
      type: Array,
      required: true,
    },
    /**
     * This represents a value of the initial state of
     * the table as desired by the user.
     */
    initialState: {
      type: Object,
      required: true,
    },
    /**
     * This determines how certain operations would be handled on the table.
     * Serverside passes all table operations to an external function provided by the user
     *  by default.
     * Clientside handles all table operations by itself unless otherwise specified.
     */
    dataType: {
      default: 'clientSide',
      validator(value) {
        return ['serverSide', 'clientSide'].indexOf(value) !== -1;
      },
    },
    /**
     * This is the column to be filtered
     */
    filterColumn: {
      type: String,
    },
    /**
     * Filter Type, Ascending or Descending
     */
    ascendingFilter: {
      type: Boolean,
      default: true,
    },
    searchQuery: {
      type: String,
    },
    /**
     * This shows the current page of the table
     */
    currentPage: {
      type: Number,
      default: 1,
    },
    /**
     * This shows the max amount of rows a table will have
     */
    pageSize: {
      type: Number,
      default: 20,
    },
    paginate: {
      type: Boolean,
      default: false,
    },
    /**
     * WIP
     * This should contain
     * total items
     * current page
     * getData function (promise)
     */
    paginationProps: {
      type: Object,
      default() {
        return {};
      },
    },
    showChild: {
      type: [Boolean, Number],
      default: false,
    },
    topColumns: {
      type: Number,
      default: 5,
    },
    /**
     * This returns the data from a server
     */
    getServerData: {
      type: Function,
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    maxSize: {
      type: Number,
      default: 0,
    },
  },
  watch: {
    currentPage(newData, oldData) {
      this.getPageData();
    },
    filterColumn(newData, oldData) {
      this.filterData();
    },
    ascendingFilter(newData, oldData) {
      this.filterData();
    },
    searchQuery(newData, oldData) {
      this.searchModifyRows(newData);
    },
    loading(newData, oldData) {
      if (!this.loading) {
        this.getPageData();
        this.getRows();
      }
    },
  },
  data() {
    return {
      // The table's state
      state: this.initialState,
      /**
       * This holds the columns for the table.
       * Each object in this array contains the following:
       * 1. id
       * 2. isVisible (Bool) tells us if visible or not
       * 3. toggleVisibility (func) allows us to toggle visibility
       * 4. value
       * 5. colspan??: colspan for column default is 1
       */
      headers: [],
      /**
       * @param rows
       * This holds the rows for the table.
       * Each object in this array contains the following:
       * 1. id
       * 2. cells (Array) contains an array of VISIBLE cell objects
       * 3. allCells (Array) contains an array of ALL cell objects
       *
       * EACH CELL OBJECT CONTAINS THE FOLLOWING:
       * 1. id
       * 2. column: Index of the column
       * 3. row: Index of the row
       * 4. value: value of the cell
       */
      rows: [],
      // loading state of the table
      loadingData: false,
    };
  },
  mounted() {
    this.getHeader();
    if (this.dataType === 'clientSide') {
      this.getPageData();
      this.getRows();
    }
  },
  methods: {
    /**
     * @function getHeader
     * @desc This returns the table header based on current state
     *
     */
    getHeader() {
      if (this.columns.length) {
        const headers = this.columns.map((column, index) => ({
          id: index + 1,
          isVisible: true,
          toggleVisibility: this.toggleVisibility,
          value: typeof column === 'object' ? column.value : column,
          colspan: 1,
        }));
        this.headers = headers;
      }
    },
    /**
     * @function toggleVisibility
     * @desc Toggles columns data visibility
     *
     */
    toggleVisibility(id) {
      if (this.headers.length) {
        const newHeader = this.headers.map((header) => {
          if (header.id === id) {
            // eslint-disable-next-line no-param-reassign
            header.isVisible = !header.isVisible;
          }
          return header;
        });
        this.headers = Object.assign(this.headers, newHeader);
      }
    },
    /**
     * @function getRows
     * @desc This returns an array of row objects
     *
     */
    getRows() {
      const newRows = [];
      this.rows.forEach((element) => {
        Vue.set(element, 'isVisible', true);
        newRows.push(element);
      });
      this.rows = Object.assign(this.rows, newRows);
    },
    /**
     * @function getCells
     * @desc This returns a cell object
     *
     */
    getCells(data, rowIndex, showAll = true) {
      if (data.length) {
        const newCell = data.map((singleData, index) => ({
          id: index + 1,
          column: index + 1,
          row: rowIndex,
          value: typeof singleData === 'object' ? singleData.value : singleData,
        }));
        if (showAll) {
          return newCell;
        }
      }
      return [];
    },
    getPageData() {
      if (this.dataType === 'clientSide') {
        if (this.currentPage > this.getPageIndexes()) {
          return;
        }
        const previousStart = this.currentPage > 1 ? (this.currentPage - 1) * this.pageSize : 0;
        const currentStart = this.currentPage * this.pageSize;
        this.rows = this.value.slice(previousStart, currentStart);
        this.getRows();
        this.filterData();
        return;
      }
      if (!this.loadingData) {
        this.rows = this.value;
        this.filterData();
      }
    },
    /**
     * @function getPageIndexes
     * @desc Get Page Indexes based on v-model input
     */
    getPageIndexes() {
      if (this.dataType === 'clientSide') {
        return Math.ceil(this.value.length / this.pageSize);
      }
      return Math.ceil(this.maxSize / this.pageSize);
    },
    /**
     * @function filterData
     * @desc Modifies the existing Row Data and sends it bak
     * @param key: String => the column key
     * @param ascending: Boolean
     *
     */
    filterData() {
      this.rows = filterString(this.rows, {
        value: this.filterColumn,
        ascending: this.ascendingFilter,
      });
      if (!this.ascendingFilter) {
        this.rows = this.rows.reverse();
      }
    },

    /**
     * @function searchModifyRows
     * @desc Modifies exisitng row data based on search query
     *
     */
    searchModifyRows(searchQuery) {
      if (searchQuery === '') {
        this.getRows();
        return;
      }
      const search = searchQuery.toUpperCase();
      for (let i = 0; i < this.rows.length; i += 1) {
        let concatRow = [];
        Object.values(this.rows[i]).forEach((element) => {
          concatRow.push(element);
        });
        concatRow = concatRow.toString();
        if (concatRow.toUpperCase().includes(search)) {
          Vue.set(this.rows[i], 'isVisible', true);
        } else {
          Vue.set(this.rows[i], 'isVisible', false);
        }
      }
      this.rows = sortSearch(this.rows, searchQuery);
    },
    /**
     * @function step
     * @desc This returns a cell object
     * @param forward BOOLEAN. if true, paginate forward; otherwise backward
     *
     */
    step(forward) {
      if (forward) {
        if (this.currentPage === this.getPageIndexes()) {
          return;
        }
        const newPage = this.currentPage + 1;
        this.$emit('updatePage', newPage);
      } else {
        if (this.currentPage === 1) {
          return;
        }
        const newPage = this.currentPage - 1;
        this.$emit('updatePage', newPage);
      }
    },
  },
  render() {
    return this.$scopedSlots.default({
      state: this.state,
      headers: this.headers,
      rows: this.rows,
      filterData: this.filterData,
      getPageData: this.getPageData,
      pages: this.getPageIndexes(),
      step: this.step,
      loadingData: this.loadingData,
      getServerData: this.getServerData,
    });
  },
};
